import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeModal from "@/components/UpgradeModal";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Download, Edit, Trash2, FileText, Clock, Filter, TrendingUp, RotateCcw } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { de, enUS } from "date-fns/locale";

export default function Invoices() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language === "de" ? de : enUS;
  
  const [invoices, setInvoices] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [autoCreateDialogOpen, setAutoCreateDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedProjectForAuto, setSelectedProjectForAuto] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [statusFilter, setStatusFilter] = useState("all");
  const [formData, setFormData] = useState({
    customer_id: "",
    project_id: "",
    invoice_date: "",
    due_date: "",
    country: "DE",
    status: "draft",
    notes: "",
    items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 19, total: 0 }]
  });

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
    fetchProjects();
    fetchTimeEntries();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [invoices, selectedMonth, statusFilter]);

  const filterInvoices = () => {
    let filtered = [...invoices];

    // Filter by month
    if (selectedMonth !== "all") {
      const [year, month] = selectedMonth.split("-");
      filtered = filtered.filter((inv) => {
        const invDate = new Date(inv.invoice_date);
        return invDate.getFullYear() === parseInt(year) && 
               invDate.getMonth() === parseInt(month) - 1;
      });
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((inv) => inv.status === statusFilter);
    }

    setFilteredInvoices(filtered);
  };

  const getMonthOptions = () => {
    const options = [{ value: "all", label: "Alle Monate" }];
    for (let i = 0; i < 12; i++) {
      const date = subMonths(new Date(), i);
      options.push({
        value: format(date, "yyyy-MM"),
        label: format(date, "MMMM yyyy", { locale })
      });
    }
    return options;
  };

  const calculateStats = () => {
    const total = filteredInvoices.reduce((sum, inv) => sum + inv.total, 0);
    const paid = filteredInvoices.filter(inv => inv.status === "paid").reduce((sum, inv) => sum + inv.total, 0);
    const draft = filteredInvoices.filter(inv => inv.status === "draft").reduce((sum, inv) => sum + inv.total, 0);
    const sent = filteredInvoices.filter(inv => inv.status === "sent").reduce((sum, inv) => sum + inv.total, 0);
    const open = sent; // sent but not paid = open
    
    return { total, paid, draft, open, count: filteredInvoices.length };
  };


  const fetchInvoices = async () => {
    try {
      const response = await api.get('/invoices');
      // Sort by created_at descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setInvoices(sorted);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await api.get('/time-entries');
      setTimeEntries(response.data);
    } catch (error) {
      console.error("Error fetching time entries:", error);
    }
  };

  const fetchCountries = async () => {
    // Not needed anymore, only DE
    return { DE: { name: "Deutschland", currency: "EUR" } };
  };


  const createInvoiceFromTimeEntries = async () => {
    if (!selectedProjectForAuto) {
      toast.error("Bitte wählen Sie ein Projekt");
      return;
    }

    const project = projects.find(p => p.id === selectedProjectForAuto);
    if (!project) {
      toast.error("Projekt nicht gefunden");
      return;
    }

    // Get time entries for this project
    const projectTimeEntries = timeEntries.filter(
      entry => entry.project_id === selectedProjectForAuto && entry.duration_minutes
    );

    if (projectTimeEntries.length === 0) {
      toast.error("Keine abgeschlossenen Zeiteinträge für dieses Projekt");
      return;
    }

    // Calculate total hours
    const totalMinutes = projectTimeEntries.reduce((sum, entry) => sum + entry.duration_minutes, 0);
    const totalHours = totalMinutes / 60;

    // Create invoice item with project hourly rate
    const invoiceItem = {
      description: `Arbeitszeit für ${project.name}`,
      quantity: parseFloat(totalHours.toFixed(2)),
      unit_price: project.hourly_rate || 0,
      tax_rate: 19,
      total: parseFloat((totalHours * (project.hourly_rate || 0)).toFixed(2))
    };

    // Set form data
    setFormData({
      customer_id: project.customer_id,
      project_id: project.id,
      invoice_date: format(new Date(), "yyyy-MM-dd"),
      due_date: format(new Date(Date.now() + 30*24*60*60*1000), "yyyy-MM-dd"),
      country: "DE",
      status: "draft",
      notes: `Abrechnung für ${projectTimeEntries.length} Zeiteinträge`,
      items: [invoiceItem]
    });

    setAutoCreateDialogOpen(false);
    setDialogOpen(true);
    setSelectedProjectForAuto(""); // Reset selection
    toast.success(`Rechnung aus ${totalHours.toFixed(2)} Stunden erstellt`);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Recalculate total for this item
    if (field === "quantity" || field === "unit_price") {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit_price: 0, tax_rate: 19, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
      };

      if (editingInvoice) {
        await api.put(`/invoices/${editingInvoice.id}`, data);
        toast.success(t("common.save"));
      } else {
        const response = await api.post(`/invoices`, data);
        toast.success(t("common.create"));
        
        // Mark time entries as invoiced if this was created from time tracking
        if (formData.project_id) {
          try {
            await api.post(`/time-entries/mark-invoiced?project_id=${formData.project_id}&invoice_id=${response.data.id}`);
          } catch (error) {
            console.error("Error marking time entries:", error);
          }
        }
      }

      setDialogOpen(false);
      setEditingInvoice(null);
      setFormData({
        customer_id: "",
        project_id: "",
        invoice_date: "",
        due_date: "",
        country: "DE",
        status: "draft",
        notes: "",
        items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 19, total: 0 }]
      });
      fetchInvoices();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Error saving invoice");
      }
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm(t("common.delete") + "?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("common.delete"));
      fetchInvoices();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Error deleting invoice");
      }
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success("Status aktualisiert");
      fetchInvoices();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Fehler beim Aktualisieren");
      }
    }
  };

  const resetInvoiceStatus = async (invoiceId) => {
    if (!window.confirm("Möchten Sie den Status wirklich auf 'Entwurf' zurücksetzen?")) return;
    try {
      await api.put(`/invoices/${invoiceId}`, { status: "draft" });
      toast.success("Status auf Entwurf zurückgesetzt");
      fetchInvoices();
    } catch (error) {
      toast.error("Fehler beim Zurücksetzen");
    }
  };




  const downloadPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("invoices.downloadPDF"));
    } catch (error) {
      toast.error("Error downloading PDF");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: t("invoices.status.draft"), className: "bg-gray-100 text-gray-800" },
      sent: { label: t("invoices.status.sent"), className: "bg-blue-100 text-blue-800" },
      paid: { label: t("invoices.status.paid"), className: "bg-green-100 text-green-800" },
      cancelled: { label: t("invoices.status.cancelled"), className: "bg-red-100 text-red-800" },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTaxTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.total * item.tax_rate / 100), 0);
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("invoices.title")}</h1>
          <p className="text-lg text-gray-600">{t("invoices.subtitle")}</p>
        </div>
        <div className="flex space-x-2">
          <Dialog open={autoCreateDialogOpen} onOpenChange={setAutoCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="auto-create-invoice-button">
                <Clock className="h-4 w-4 mr-2" />
                Aus Zeiteinträgen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Rechnung aus Zeiteinträgen erstellen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Projekt auswählen</Label>
                  <Select
                    value={selectedProjectForAuto}
                    onValueChange={setSelectedProjectForAuto}
                  >
                    <SelectTrigger data-testid="auto-project-select">
                      <SelectValue placeholder="Projekt wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => {
                        const projectEntries = timeEntries.filter(
                          e => e.project_id === project.id && e.duration_minutes
                        );
                        const totalHours = projectEntries.reduce((sum, e) => sum + e.duration_minutes, 0) / 60;
                        return (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name} ({totalHours.toFixed(1)}h @ {project.hourly_rate}€/h)
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={createInvoiceFromTimeEntries} 
                  className="w-full"
                  data-testid="confirm-auto-create"
                >
                  Rechnung erstellen
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Manual create dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-invoice-button" onClick={() => {
                setEditingInvoice(null);
                setFormData({
                  customer_id: "",
                  project_id: "",
                  invoice_date: format(new Date(), "yyyy-MM-dd"),
                  due_date: format(new Date(Date.now() + 30*24*60*60*1000), "yyyy-MM-dd"),
                  country: "DE",
                  status: "draft",
                  notes: "",
                  items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 19, total: 0 }]
                });
              }}>
                <Plus className="h-4 w-4 mr-2" />
                {t("invoices.newInvoice")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingInvoice ? t("common.edit") : t("invoices.newInvoice")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("invoices.customer")} *</Label>
                  <Select
                    value={formData.customer_id}
                    onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="invoice-customer-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("invoices.project")}</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                  >
                    <SelectTrigger data-testid="invoice-project-select">
                      <SelectValue placeholder={t("common.search")} />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("invoices.invoiceDate")} *</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t("invoices.dueDate")} *</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>{t("invoices.items")}</Label>
                  <Button type="button" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t("invoices.addItem")}
                  </Button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                    <Input
                      placeholder={t("invoices.description")}
                      className="col-span-4"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, "description", e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder={t("invoices.quantity")}
                      className="col-span-2"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, "quantity", parseFloat(e.target.value) || 0)}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder={t("invoices.unitPrice")}
                      className="col-span-2"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, "unit_price", parseFloat(e.target.value) || 0)}
                    />
                    <Input
                      type="number"
                      placeholder={t("invoices.taxRate")}
                      className="col-span-2"
                      value={item.tax_rate}
                      onChange={(e) => handleItemChange(index, "tax_rate", parseFloat(e.target.value) || 0)}
                    />
                    <Input
                      type="number"
                      disabled
                      className="col-span-1"
                      value={item.total.toFixed(2)}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="col-span-1"
                      onClick={() => removeItem(index)}
                      disabled={formData.items.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">{t("invoices.subtotal")}:</span>
                  <span>{calculateSubtotal().toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{t("invoices.taxTotal")}:</span>
                  <span>{calculateTaxTotal().toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>{t("invoices.grandTotal")}:</span>
                  <span>{(calculateSubtotal() + calculateTaxTotal()).toFixed(2)} €</span>
                </div>
              </div>

              <div>
                <Label>{t("invoices.notes")}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <Button type="submit" className="w-full" data-testid="save-invoice-button">
                {editingInvoice ? t("common.save") : t("common.create")}
              </Button>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>


      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Gesamt</CardTitle>
            <FileText className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{calculateStats().count}</div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().total.toFixed(2)} €</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Entwurf</CardTitle>
            <FileText className="h-5 w-5 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {filteredInvoices.filter(i => i.status === "draft").length}
            </div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().draft.toFixed(2)} €</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-orange-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Offen</CardTitle>
            <TrendingUp className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {filteredInvoices.filter(i => i.status === "sent").length}
            </div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().open.toFixed(2)} €</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-green-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Bezahlt</CardTitle>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {filteredInvoices.filter(i => i.status === "paid").length}
            </div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().paid.toFixed(2)} €</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Monat</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger data-testid="month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="draft">Entwurf</SelectItem>
                  <SelectItem value="sent">Versendet (Offen)</SelectItem>
                  <SelectItem value="paid">Bezahlt</SelectItem>
                  <SelectItem value="cancelled">Storniert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        {filteredInvoices.map((invoice, index) => (
          <Card key={invoice.id} className="card-hover" data-testid={`invoice-card-${invoice.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{invoice.invoice_number}</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{invoice.customer_name}</p>
                    {invoice.project_name && (
                      <p className="text-xs text-gray-500">{invoice.project_name}</p>
                    )}
                  </div>
                </div>
                {getStatusBadge(invoice.status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.invoiceDate")}</p>
                  <p className="font-medium">{format(new Date(invoice.invoice_date), "dd.MM.yyyy", { locale })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.dueDate")}</p>
                  <p className="font-medium">{format(new Date(invoice.due_date), "dd.MM.yyyy", { locale })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.subtotal")}</p>
                  <p className="font-medium">{invoice.subtotal.toFixed(2)} {invoice.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.grandTotal")}</p>
                  <p className="font-semibold text-lg text-emerald-600">{invoice.total.toFixed(2)} {invoice.currency}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadPDF(invoice.id)}
                  data-testid={`download-pdf-${invoice.id}`}
                >
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
                
                {/* Status update buttons */}
                {invoice.status === "draft" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateInvoiceStatus(invoice.id, "sent")}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Als versendet markieren
                  </Button>
                )}
                {invoice.status === "sent" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateInvoiceStatus(invoice.id, "paid")}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    Als bezahlt markieren
                  </Button>
                )}
                
                {/* Reset status button - show if not already draft */}
                {invoice.status !== "draft" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resetInvoiceStatus(invoice.id)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    title="Status auf Entwurf zurücksetzen"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Zurücksetzen
                  </Button>
                )}
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteInvoice(invoice.id)}
                  data-testid={`delete-invoice-${invoice.id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredInvoices.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            {t("common.noData")}
          </CardContent>
        </Card>
      )}
      

    <UpgradeModal

      isOpen={upgradeModalOpen}

      onClose={() => setUpgradeModalOpen(false)}

      message={upgradeMessage}

      currentPlan={user?.plan}

    />

    </div>
  );
}